import { useEffect, useState, useRef } from "react";
import { TextAaIcon, CaretDownIcon, CheckIcon } from "@phosphor-icons/react";

const PRESETS = [
  { key: "xs", label: "Extra small", scale: 0.85 },
  { key: "sm", label: "Small",       scale: 0.95 },
  { key: "md", label: "Medium",      scale: 1.00 },
  { key: "lg", label: "Large",       scale: 1.15 },
  { key: "xl", label: "Extra large", scale: 1.30 },
];

const STORAGE_KEY = "app.fontScale";

function readInitial() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v) {
      const found = PRESETS.find((p) => p.key === v);
      if (found) return found;
    }
  } catch (e) {
    console.warn("Font scale read from localStorage failed", e);
  }
  return PRESETS[2]; // Medium
}

function applyScale(scale) {
  document.documentElement.style.setProperty("--font-scale", String(scale));
}

export default function FontSizeControl() {
  const [current, setCurrent] = useState(readInitial);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    applyScale(current.scale);
    try { localStorage.setItem(STORAGE_KEY, current.key); }
    catch (e) { console.warn("Font scale save failed", e); }
  }, [current]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="font-size-control">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 h-9 px-3 border border-slate-300 rounded-none bg-white hover:border-slate-900 transition-colors"
        title="Change website font size"
        data-testid="font-size-trigger"
      >
        <TextAaIcon size={16} weight="regular" className="text-slate-700" />
        <span className="text-sm font-semibold uppercase tracking-wide text-slate-700">{current.key}</span>
        <CaretDownIcon size={12} className="text-slate-500" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-300 shadow-lg z-50"
          data-testid="font-size-menu"
        >
          <div className="px-3 py-2 border-b border-slate-200 overline text-slate-500">Font size</div>
          {PRESETS.map((p) => {
            const active = p.key === current.key;
            return (
              <button
                key={p.key}
                onClick={() => { setCurrent(p); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${active ? "bg-slate-50" : ""}`}
                data-testid={`font-size-option-${p.key}`}
              >
                <span
                  className="text-slate-900"
                  style={{ fontSize: `${Math.max(9, Math.min(18, 14 * p.scale))}px` }}
                >
                  {p.label}
                </span>
                {active && <CheckIcon size={14} className="text-slate-900" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
